import type { RouteStop } from '../types';

export const TASTING_STOPS: RouteStop[] = [
  {
    id: 'black-tie-tours',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'willamette-valley-truffle-corridor',
    name: 'Black Tie Tours',
    description:
      'A Newberg truffle tour company offering seasonal dog-led outings with lunch and wine-country framing.',
    address: 'Downtown pickup in Newberg',
    city: 'Newberg',
    state: 'OR',
    lat: 45.3009,
    lng: -122.9732,
    hours: 'Truffle tours are listed as seasonal, usually late January through March. Private-tour inquiries are invited on the official site.',
    website: 'https://www.blacktietours.com/truffletours',
    phone: '971-832-0436',
    highlights: ['Guided forest hunts', 'Truffle dog team', 'Lunch and wine pairing', 'Private inquiries welcome'],
    experiences: ['Guided Hunt'],
    visitorPolicy: 'seasonal',
    story:
      'Black Tie Tours provides a structured Newberg-area hunt option for travelers who want a scheduled, guided experience.',
    season: 'Late January through March on the current official tour schedule.',
    bookingRequired: true,
    routeNotes: [
      'Useful as a Newberg-area activity for winter-focused itineraries.',
      'Can be paired with a stay or dinner stop in the same part of the valley.',
    ],
    verificationNote:
      'Current official pages support winter tours and private inquiries, but do not currently support a June or July public-availability claim.',
    sourceUrls: [
      'https://www.blacktietours.com/truffletours',
      'https://www.blacktietours.com/about-us',
    ],
  },
  {
    id: 'oregon-fresh-truffle-marketplace',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'willamette-valley-truffle-corridor',
    name: 'Oregon Fresh Truffle Marketplace',
    description:
      'A winter event in McMinnville tied to Oregon Truffle Festival programming, vendors, and seasonal food traffic.',
    address: '455 NE Irvine St',
    city: 'McMinnville',
    state: 'OR',
    lat: 45.2111,
    lng: -123.1899,
    hours: 'The 2026 marketplace is listed for Saturday, February 28, 11:00am-4:00pm.',
    website: 'https://oregontrufflefestival.org/event/oregon-fresh-truffle-marketplace/',
    phone: '(503) 472-6206',
    highlights: ['Festival marketplace', 'Winter event', 'McMinnville venue', 'Wine-country audience'],
    experiences: ['Seasonal Event', 'Farm Market'],
    visitorPolicy: 'seasonal',
    story:
      'For travelers who want a public-facing winter truffle event rather than a guided hunt, this is a relevant McMinnville option.',
    season: 'February event window tied to Oregon Truffle Festival programming.',
    bookingRequired: true,
    routeNotes: [
      'Useful as an event-day stop in McMinnville during festival season.',
      'Best treated as a seasonal event rather than a year-round listing.',
    ],
    verificationNote:
      'The 2026 event listing is verified on the official festival site and tied to The Ground at Stillwater in McMinnville.',
    sourceUrls: [
      'https://oregontrufflefestival.org/event/oregon-fresh-truffle-marketplace/',
      'https://oregontrufflefestival.org/our-otf-mission/',
    ],
  },
  {
    id: 'jory-restaurant',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'willamette-valley-truffle-corridor',
    name: 'JORY Restaurant',
    description:
      "The Allison's signature dining room, suited to the more formal side of a truffle weekend.",
    address: '2525 Allison Lane',
    city: 'Newberg',
    state: 'OR',
    lat: 45.3197,
    lng: -122.9449,
    hours: 'Open daily with breakfast, brunch, a la carte service, and dinner tasting menus according to the official site.',
    website: 'https://theallison.com/jory/',
    phone: '(503) 554-2526',
    highlights: ['Signature restaurant', 'Seasonal tasting menu', 'Luxury resort setting', 'Wine-country dining'],
    experiences: ['Truffle Dinner'],
    visitorPolicy: 'open-to-public',
    story:
      'JORY gives the guide a Newberg dining option that can be used with or without an overnight resort stay.',
    season: 'Year-round dining with menus that change around seasonal ingredients.',
    bookingRequired: true,
    routeNotes: [
      'Useful as a Newberg dinner option even without menu-level truffle confirmation.',
      'Best framed as seasonal fine dining rather than a guaranteed truffle-menu experience.',
    ],
    verificationNote:
      'Official site confirms the restaurant, hours, and tasting-menu format, but not specific current truffle dishes.',
    sourceUrls: ['https://theallison.com/jory/', 'https://theallison.com/jory/menus/'],
  },
  {
    id: 'joel-palmer-house',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-22',
    region: 'willamette-valley-truffle-corridor',
    name: 'The Joel Palmer House Restaurant',
    description:
      'A Dayton restaurant known for long-running wild-mushroom and seasonal tasting-menu dining.',
    address: '600 Ferry Street',
    city: 'Dayton',
    state: 'OR',
    lat: 45.218,
    lng: -123.0798,
    hours: 'Tuesday-Saturday, 5:00pm-close on the official contact page.',
    website: 'https://www.joelpalmerhouse.com/',
    phone: '(503) 864-2995',
    highlights: ['Dayton anchor', 'Seasonal tasting menu', 'Wild mushroom lineage', 'Wine-pairing credibility'],
    experiences: ['Truffle Dinner'],
    visitorPolicy: 'open-to-public',
    story:
      'Joel Palmer House gives the guide a Dayton dining stop with an established mushroom-focused identity.',
    season: 'Year-round restaurant with a seasonally changing tasting menu.',
    bookingRequired: true,
    routeNotes: [
      'Useful as a Dayton dinner stop in a two- or three-day itinerary.',
      'Relevant to the guide because of its long-running mushroom-focused reputation.',
    ],
    verificationNote:
      'Official site confirms the restaurant, Dayton location, hours, and foraged-mushroom orientation. Specific seasonal truffle dishes still need menu-level confirmation.',
    sourceUrls: ['https://www.joelpalmerhouse.com/', 'https://www.joelpalmerhouse.com/contact/'],
  },
  {
    id: 'truffle-dog-company-private-adventures',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'willamette-valley-truffle-corridor',
    name: 'Truffle Dog Company Private Adventures',
    description:
      'A private-adventure operator with Oregon truffle experiences listed during the December-through-May season.',
    address: 'Pacific Northwest bookings / Oregon adventures listed seasonally',
    city: 'Willamette Valley',
    state: 'OR',
    lat: 45.332,
    lng: -123.04,
    hours: 'Private adventures are described as operating during truffle season from December through May on the official site, with separate spring field classes also listed.',
    website: 'https://truffledogcompany.com/bespoke/',
    highlights: ['Private adventures', 'Dog-led hunts', 'Spring shoulder season', 'Seasonal bookings'],
    experiences: ['Guided Hunt'],
    visitorPolicy: 'by-appointment',
    story:
      'This listing helps the guide represent a spring shoulder-season option because the official site describes private adventures through May.',
    season: 'Official copy supports December through May private adventures, with additional spring field instruction listed in March, April, and May.',
    bookingRequired: true,
    routeNotes: [
      'Best framed as a private, by-appointment option rather than a drop-in public tour.',
      'Useful for spring-through-May planning, but not for a broad June or July claim.',
    ],
    verificationNote:
      'Truffle Hunting Oregon is not affiliated with this operator. Listing inclusion only reflects what is described on current official pages, which support December-through-May private adventures and spring field instruction but do not justify a broad summer claim.',
    sourceUrls: [
      'https://truffledogcompany.com/bespoke/',
      'https://truffledogcompany.com/product/advanced-field-class-spring-2024/',
      'https://truffledogcompany.com/',
    ],
  },
  {
    id: 'truffle-connection-pnw-forays',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'portland-foothills-truffle-zone',
    name: 'Truffle Connection PNW Forays',
    description:
      'A Portland-access truffle outing format built around seasonal dog-led forest forays, lunch, and winery stops.',
    address: 'Portland pickup with Estacada-area forest access described on official event pages',
    city: 'Portland',
    state: 'OR',
    lat: 45.5152,
    lng: -122.6784,
    hours: 'Official foray pages describe seasonal small-group outings with Portland departures and Estacada forest access.',
    website: 'https://www.truffleconnectionpnw.com/forays',
    highlights: ['Portland departure', 'Estacada forest access', 'Dog-led demonstration', 'Seasonal forays'],
    experiences: ['Guided Hunt'],
    visitorPolicy: 'seasonal',
    story:
      'This gives the guide a Portland-area truffle outing that can work as a day trip without asking readers to shift their base into wine country.',
    season: 'Current official event pages support winter-season forays and example group outings tied to scheduled dates.',
    bookingRequired: true,
    routeNotes: [
      'Useful as a Portland-access outing for readers who want a guided forest experience with a defined pickup pattern.',
      'Should be framed as operator-scheduled and seasonal rather than as an always-available departure.',
    ],
    verificationNote:
      'Official pages support Portland pickup language, Estacada forest access, dog demonstrations, and lunch-and-wine outing structure. The public guide should still avoid implying that every itinerary date is continuously available.',
    sourceUrls: [
      'https://www.truffleconnectionpnw.com/forays',
      'https://www.truffleconnectionpnw.com/forays/2025/2/1/truffle-hunting-foray-truffle-connection',
      'https://www.truffleconnectionpnw.com/',
    ],
  },
  {
    id: 'psu-portland-farmers-market-truffle-stand',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'portland-foothills-truffle-zone',
    name: 'PSU Portland Farmers Market Truffle Stand',
    description:
      'A seasonal Portland market stop where Truffle Connection PNW lists fresh native truffles, infused products, samples, and public-facing education.',
    address: 'Portland State University Farmers Market',
    city: 'Portland',
    state: 'OR',
    lat: 45.5124,
    lng: -122.6841,
    hours:
      'Current Truffle Connection event listings show Saturday market dates in January and February 2026 at Portland State University.',
    website: 'https://www.truffleconnectionpnw.com/forays',
    phone: '503-680-0965',
    highlights: ['Portland market stop', 'Fresh truffles', 'Public samples', 'Seasonal Saturdays'],
    experiences: ['Farm Market', 'Retail Stop'],
    visitorPolicy: 'seasonal',
    story:
      'This adds a second public Portland anchor for readers who want a lower-commitment way to encounter Oregon truffles without booking a full forest outing.',
    season: 'Official event listings support a January-through-February Saturday market pattern in 2026.',
    bookingRequired: false,
    routeNotes: [
      'Useful as a Portland city stop for readers who want to smell, buy, or ask about truffles without committing to a guided hunt.',
      'Best treated as a seasonal market pattern tied to listed Saturday dates rather than as an evergreen retail shop.',
    ],
    verificationNote:
      'Current Truffle Connection pages support recurring PSU market appearances with fresh truffles, infused products, samples, and educational material. Specific market dates should still be checked before travel.',
    sourceUrls: [
      'https://www.truffleconnectionpnw.com/forays',
      'https://www.truffleconnectionpnw.com/',
    ],
  },
  {
    id: 'north-american-truffling-society-public-meetings',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'corvallis-truffle-science-zone',
    name: 'North American Truffling Society Public Meetings',
    description:
      'A Corvallis-based educational anchor centered on public truffle talks and society meetings rather than polished visitor programming.',
    address: 'Cordley Hall, 2701 SW Campus Way',
    city: 'Corvallis',
    state: 'OR',
    lat: 44.5659,
    lng: -123.2794,
    hours:
      'NATS currently says meetings are open to the public, usually held at OSU Cordley Hall, with timing posted through the society.',
    website: 'https://www.natruffling.org/index1.htm',
    highlights: ['Corvallis research anchor', 'Public meetings', 'OSU setting', 'Education-first'],
    experiences: ['Seasonal Event'],
    visitorPolicy: 'seasonal',
    story:
      'This is the clearest Corvallis truffle anchor currently supported by primary sources, but it still reads more like a society program than a finished travel stop.',
    season: 'Public meetings and occasional seminars are described by NATS, but the schedule remains event-based rather than visitor-oriented.',
    bookingRequired: false,
    routeNotes: [
      'Useful for readers interested in truffle science, public talks, and a more educational version of the route.',
      'Best framed as a society calendar anchor rather than a polished tourist attraction.',
    ],
    verificationNote:
      'NATS says meetings are open to the public and usually held in Corvallis, while most forays remain member-restricted. That makes this a narrow but publishable educational stop for the academic leg of the guide.',
    sourceUrls: [
      'https://www.natruffling.org/index1.htm',
      'https://www.natruffling.org/faq.htm',
      'https://events.oregonstate.edu/cordley_hall',
    ],
  },
  {
    id: 'nats-annual-potluck-dinner',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'corvallis-truffle-science-zone',
    name: 'NATS Annual Potluck Dinner',
    description:
      'A seasonal Corvallis gathering built around the North American Truffling Society community, scholarship fundraising, and a speaker-led evening.',
    address: 'Corvallis Community Center, 2601 NW Tyler Avenue',
    city: 'Corvallis',
    state: 'OR',
    lat: 44.5904,
    lng: -123.2812,
    hours:
      'The current NATS winter 2026 newsletter lists the annual potluck for February 14, 2026 at 6:00pm in Corvallis.',
    website: 'https://www.natruffling.org/news/trufflerwinter2026.pdf',
    highlights: ['Annual gathering', 'Scholarship fundraiser', 'Corvallis community center', 'Seasonal event'],
    experiences: ['Seasonal Event'],
    visitorPolicy: 'seasonal',
    story:
      'This gives Corvallis a second current event anchor, but it remains a society-centered gathering rather than a conventional public tasting stop.',
    season: 'Current primary-source support is for a February annual event tied to the NATS winter calendar.',
    bookingRequired: false,
    routeNotes: [
      'Useful as a seasonal academic-community event for readers who want the society side of Oregon truffles, not only the commercial route.',
      'Best treated as a calendar-based gathering that may suit members, guests, or invited attendees rather than a standard drop-in attraction.',
    ],
    verificationNote:
      'Current NATS newsletters document the 2026 annual potluck in Corvallis and describe it as part of the society calendar. Attendance expectations should still be checked with NATS before travel.',
    sourceUrls: [
      'https://www.natruffling.org/news/trufflerwinter2026.pdf',
      'https://www.natruffling.org/news/trufflerspring2026.pdf',
      'https://www.natruffling.org/scholar.htm',
    ],
  },
  {
    id: 'joriad-north-american-truffle-dog-championship',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'eugene-festival-truffle-zone',
    name: 'The Joriad North American Truffle Dog Championship',
    description:
      'A Eugene spectator event built around amateur truffle-dog competition at the start of Oregon Truffle Festival season.',
    address: 'Lane Events Center, 796 W 13th Ave',
    city: 'Eugene',
    state: 'OR',
    lat: 44.0467,
    lng: -123.1089,
    hours: 'The 2026 event is listed for Friday, February 6, with morning spectator hours at Lane Events Center.',
    website: 'https://oregontrufflefestival.org/the-joriad-championship/',
    highlights: ['Eugene event', 'Spectator access', 'Truffle dogs', 'Festival programming'],
    experiences: ['Seasonal Event'],
    visitorPolicy: 'seasonal',
    story:
      'The Joriad is the clearest Eugene-area truffle event with public spectator access and repeat annual structure.',
    season: 'Official 2026 event listing supports a February Eugene festival window.',
    bookingRequired: true,
    routeNotes: [
      'Useful as a Eugene event stop because it gives the southern zone a public-facing truffle anchor.',
      'Best framed as a ticketed seasonal event rather than a year-round stop.',
    ],
    verificationNote:
      'Official Oregon Truffle Festival pages support Eugene location, spectator attendance, and annual championship framing. It should stay tied to specific event dates rather than general evergreen wording.',
    sourceUrls: [
      'https://oregontrufflefestival.org/the-joriad-championship/',
      'https://oregontrufflefestival.org/event/the-2026-joriad-north-american-truffle-dog-championship/',
    ],
  },
  {
    id: 'otf-two-day-truffle-dog-training',
    type: 'route-stop',
    verified: true,
    lastVerified: '2026-04-23',
    region: 'eugene-festival-truffle-zone',
    name: 'OTF Two-Day Truffle Dog Training',
    description:
      'A small-group Oregon Truffle Festival training intensive held west of Eugene with classroom work, forest training, and a guided hunt format.',
    address: 'Wine country west of Eugene, exact training-site details provided after registration',
    city: 'Eugene',
    state: 'OR',
    lat: 44.0521,
    lng: -123.0868,
    hours: 'Official 2026 event pages list February and late-February into March training weekends.',
    website: 'https://oregontrufflefestival.org/truffle-dog-training/',
    highlights: ['Small-group training', 'Forest work', 'Dog handling', 'Festival program'],
    experiences: ['Seasonal Event'],
    visitorPolicy: 'seasonal',
    story:
      'This gives Eugene a second official truffle-travel anchor, especially for readers interested in training rather than spectator events alone.',
    season: 'Official 2026 event pages support late-winter and early-March training weekends west of Eugene.',
    bookingRequired: true,
    routeNotes: [
      'Useful as a Eugene stop for dog-training travelers and more hands-on truffle visitors.',
      'Best framed as a scheduled instructional event, not a casual drop-in experience.',
    ],
    verificationNote:
      'Official Oregon Truffle Festival pages support the two-day training format, forest component, and west-of-Eugene setting. It should remain tied to listed event weekends and not be generalized beyond those dates.',
    sourceUrls: [
      'https://oregontrufflefestival.org/truffle-dog-training/',
      'https://oregontrufflefestival.org/event/2026-two-day-truffle-dog-training-february-28-march-1/',
      'https://oregontrufflefestival.org/event/2026-two-day-truffle-dog-training-february-7-8/',
    ],
  },
];
